import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import '../css/App.css';
import '../css/tour.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers,
  faClipboardList,
  faCookieBite,
  faHome,
  faPlus,
  faCreditCard,
  faTachometerAlt,
  faCheckCircle,
  faFileAlt,
  faBell,
  faDatabase,
  faIdCard,
  faBuilding,
  faChartBar,
  faCookie,
  faShieldAlt,
  faQuestionCircle,
  faChevronDown,
  faUserCircle,
  faSignOutAlt,
  faBullhorn,
} from '@fortawesome/free-solid-svg-icons';
import { getVersion } from './versionService';
// import TranslateSelector from './TranslateSelector';
import SubscriptionModal from './SubscriptionModal';
import Swal from 'sweetalert2';


function VersionLabel() {
  const [v, setV] = useState('...');
  useEffect(() => {
    let mounted = true;
    async function load() {
      const ver = await getVersion();
      if (mounted) setV(ver);
    }
    load();
    const tid = setInterval(load, 10000);
    return () => { mounted = false; clearInterval(tid); };
  }, []);

  return (
    <a
      href="/version"
      onClick={(e) => {
        e.preventDefault();
        try {
          window.history.pushState({}, '', '/version');
          window.dispatchEvent(new PopStateEvent('popstate'));
        } catch (_) { }
      }}
      className="version-link"
      style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.72rem', letterSpacing: '0.04em' }}
      title="Version"
    >
      v{v}
    </a>
  );
}

/** Helper to format user display name gracefully */
const getFormattedUserName = (user) => {
  if (!user) return 'User';
  let rawName = user.name || user.full_name || user.fullName || user.username;
  if (rawName && String(rawName).trim().toLowerCase() !== 'user' && String(rawName).trim().toLowerCase() !== 'admin' && String(rawName).trim().toLowerCase() !== 'manager') {
    return String(rawName)
      .trim()
      .split(' ')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }
  if (user.email) {
    const emailPrefix = user.email.split('@')[0];
    const cleaned = emailPrefix.replace(/[._-]/g, ' ').trim();
    if (cleaned) {
      return cleaned
        .split(' ')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
    }
  }
  return 'User';
};

/** Helper to format user role gracefully */
const getFormattedUserRole = (user) => {
  if (!user || !user.role) return 'User';
  const r = String(user.role).trim().toLowerCase();
  if (r === 'admin') return 'Admin';
  if (r === 'manager') return 'Manager';
  if (r === 'user') return 'User';
  return r.charAt(0).toUpperCase() + r.slice(1);
};

/** Reusable nav link with icon + label + tooltip */
function NavItem({ href, icon, label, onClick, color, isActive, tourPulse, tooltip }) {
  return (
    <div className="nav-tooltip-wrap">
      <a
        href={href}
        className={`base-menu-item ${isActive ? 'active' : ''} ${tourPulse ? 'tour-nav-pulse' : ''}`}
        onClick={onClick}
        aria-label={label}
      >
        <FontAwesomeIcon icon={icon} className="nav-icon" style={{ color: color || 'rgba(255, 255, 255, 0.92)' }} />
        <span className="nav-label">{label}</span>
      </a>
      <span className="nav-tooltip-box">{tooltip || label}</span>
    </div>
  );
}

/** Help & PDF download modal */
function HelpPdfModal({ onClose, onStartTour, role }) {
  const pdfGuides = [
    { name: 'User Guide', desc: 'Full platform walkthrough', emoji: '📘', file: 'OneDPDP_User_Guide.pdf' },
    { name: 'DSAR Handbook', desc: 'Data Subject Access Request guide', emoji: '🛡️', file: 'OneDPDP_DSAR_Guide.pdf' },
    { name: 'Compliance Checklist', desc: 'DPDP Act 2023 readiness list', emoji: '✅', file: 'OneDPDP_Compliance_Checklist.pdf' },
  ];

  // Escape key closes modal
  React.useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="help-pdf-overlay" onClick={onClose}>
      <div className="help-pdf-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Help and documentation">
        {/* Header */}
        <div className="help-pdf-modal-header">
          <div className="help-pdf-modal-header-left">
            <div className="help-pdf-modal-icon">📚</div>
            <div>
              <div className="help-pdf-modal-title">Help & Documentation</div>
              <div className="help-pdf-modal-subtitle"> guides or start an interactive tour</div>
            </div>
          </div>
          <button className="help-pdf-close-btn" onClick={onClose} aria-label="Close help modal">×</button>
        </div>

        {/* Body */}
        <div className="help-pdf-modal-body">
          {/* PDF Downloads */}
          {/* <div>
            <div className="help-pdf-section-label">📄 PDF Guides</div>
            <div className="help-pdf-section-desc">Download the complete documentation for offline reference.</div>
            <div className="help-pdf-download-list">
              {pdfGuides.map((guide) => (
                <a
                  key={guide.file}
                  href={`${process.env.PUBLIC_URL}/docs/${guide.file}`}
                  download={guide.file}
                  className="help-pdf-download-item"
                  onClick={(e) => {
                    // If file doesn't exist, prevent broken download and show info
                    e.stopPropagation();
                  }}
                >
                  <div className="help-pdf-download-item-icon">{guide.emoji}</div>
                  <div className="help-pdf-download-item-text">
                    <div className="help-pdf-download-item-name">{guide.name}</div>
                    <div className="help-pdf-download-item-size">{guide.desc}</div>
                  </div>
                  <span className="help-pdf-download-badge">↓ PDF</span>
                </a>
              ))}
            </div>
          </div> */}

          {/* Divider */}

          {/* Interactive Tour */}
          <div className="help-tour-card">
            <div className="help-tour-card-icon">🎯</div>
            <div className="help-tour-card-text">
              <div className="help-tour-card-title">Interactive Page Tour</div>
              <div className="help-tour-card-desc">Step-by-step guide highlighting every feature on this page.</div>
            </div>
            <button
              className="help-tour-start-btn"
              onClick={() => { onClose(); onStartTour(); }}
            >
              Start Tour →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BasePage({ children, onLogout, onMenu, user, setManagerPage }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showSubscription, setShowSubscription] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [tourStepIndex, setTourStepIndex] = useState(0);
  const [consentDropdownOpen, setConsentDropdownOpen] = useState(false);
  const [managerDropdownOpen, setManagerDropdownOpen] = useState(false);
  const [cookieDropdownOpen, setCookieDropdownOpen] = useState(false);
  const [userCookiesDropdownOpen, setUserCookiesDropdownOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);

  const toggleDropdown = (dropdownKey) => {
    setConsentDropdownOpen(prev => dropdownKey === 'consent' ? !prev : false);
    setManagerDropdownOpen(prev => dropdownKey === 'manager' ? !prev : false);
    setCookieDropdownOpen(prev => dropdownKey === 'cookie' ? !prev : false);
    setUserCookiesDropdownOpen(prev => dropdownKey === 'userCookies' ? !prev : false);
    setUserDropdownOpen(prev => dropdownKey === 'user' ? !prev : false);
  };

  useEffect(() => {
    const handleLocationChange = () => {
      setCurrentPath(window.location.pathname);
    };
    window.addEventListener('popstate', handleLocationChange);
    return () => window.removeEventListener('popstate', handleLocationChange);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.nav-dropdown-container') && !e.target.closest('.user-profile-container')) {
        setConsentDropdownOpen(false);
        setManagerDropdownOpen(false);
        setCookieDropdownOpen(false);
        setUserCookiesDropdownOpen(false);
        setUserDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleSelectConsentItem = (path, pageKey) => {
    window.history.pushState({}, '', path);
    setCurrentPath(path);
    if (onMenu) onMenu(pageKey);
    setConsentDropdownOpen(false);
    setMobileMenuOpen(false);
  };

  const isConsentActive = currentPath === '/user/consent' || currentPath === '/user/consent-overview' || currentPath === '/user/purpose-consent' || currentPath === '/user/logs';
  const isManagerDropdownActive = ['/manager/notification', '/manager/Data_Types', '/manager/Department'].includes(currentPath);
  const isCookieDropdownActive = ['/manager/cookie-consent', '/manager/consent-banner'].includes(currentPath);
  const isUserCookiesActive = currentPath === '/user/cookies-notify' || currentPath === '/user/consent-banner';

  const normalizeTourPath = (pathname = '') => {
    const path = String(pathname || window.location.pathname || '/').split('?')[0].split('#')[0].trim();
    if (!path || path === '/') return '/';
    return path.endsWith('/') && path.length > 1 ? path.slice(0, -1) : path;
  };

  const isVisibleElement = (element) => {
    if (!element) return false;
    if (element.closest('.tour-overlay')) return false;
    if (element.tagName === 'BODY' || element.tagName === 'HTML') return false;
    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  };

  const buildInteractiveGuideSteps = (pathname, role) => {
    const p = normalizeTourPath(pathname).toLowerCase();
    const baseSteps = [];

    if (role === 'admin') {
      if (p.startsWith('/admin/cookie-consent')) {
        baseSteps.push(
          { icon: '🍪', title: 'Cookie consent setup', body: 'Use this screen to review and update the cookie consent template for your tenant.', target: '.base-main' },
          { icon: '💾', title: 'Save changes', body: 'After reviewing the content, save your changes so the tenant settings take effect.', target: '.base-main' },
        );
      } else {
        baseSteps.push(
          { icon: '🏢', title: 'Tenant workspace', body: 'This page lists the tenants available to your admin account and their key details.', target: '.base-main' },
          { icon: '🧭', title: 'Switch sections', body: 'Use the top navigation to jump between tenant and cookie consent pages quickly.', target: '.base-menu-item' },
        );
      }
    } else if (role === 'manager') {
      if (p.startsWith('/manager/users')) {
        baseSteps.push(
          { icon: '👥', title: 'User management', body: 'Manage organization accounts, roles, access, and account status from this section.', target: '.base-main' },
          { icon: '➕', title: 'Add User', body: 'Click this button to add a new user — you will be asked for their email and full name.', target: '.add-user-button' },
          { icon: '⬅️', title: 'Back', body: 'Use this button to go back to the tenant/dashboard view.', target: '.back-button' },
          { icon: '📊', title: 'User stats cards', body: 'These cards show total, active, and inactive user counts. Click a card to filter the table below.', target: '.stats-grid' },
          { icon: '🔍', title: 'Search users', body: 'Search for a user by name or email using this box.', target: '.search-box' },
          { icon: '🏷️', title: 'Filter status', body: 'Use this dropdown to filter the list by active or inactive users.', target: '.filter-box' },
          { icon: '✏️', title: 'Edit user', body: 'Click the pencil icon to update a user\'s full name.', target: '.edit-action' },
          { icon: '✉️', title: 'Reset password', body: 'Click the envelope icon to reset and email a new password to this user.', target: '.email-action' },
          { icon: '📜', title: 'Activity logs', body: 'Click the history icon to view this user\'s audit/activity logs.', target: '.logs-action' },
          { icon: '🗑️', title: 'Delete user', body: 'Click this icon to permanently remove the user account.', target: '.delete-action' },
        );
      } else if (p.startsWith('/manager/consent')) {
        baseSteps.push(
          { icon: '🧭', title: 'Navigation', body: 'Use the manager navigation to move between consent forms and the rest of your workspace.', target: '.tour-target-manager-consent-nav' },
          { icon: '➕', title: 'Create consent', body: 'Use the Create Consent button to start a new consent form from the top of the page.', target: '.tour-target-create-consent-btn' },
          { icon: '📋', title: 'Consent forms', body: 'Use this page to review and update the consent forms used by your team.', target: '.tour-target-consent-card' },
          { icon: '👁️', title: 'Edit and preview', body: 'Use the edit action on a consent card to open the editor and preview the content before saving.', target: '.tour-target-edit-action' },
          { icon: '🏷️', title: 'Version management', body: 'Use the version badge on each card to manage the consent form version and status.', target: '.tour-target-version-badge' },
          { icon: '💾', title: 'Save and publish', body: 'After editing, use the editor actions to save and publish the updated consent form.', target: '.tour-target-edit-action' },
        );
      } else if (p.startsWith('/manager/cookie')) {
        baseSteps.push(
          { icon: '🍪', title: 'Cookie settings', body: 'Review the cookie consent messaging and adjust the template as needed.', target: '.tour-target-cookie-design' },
          { icon: '🖥️', title: 'Preview widget', body: 'Check the live preview panel to see how your cookie settings look before saving.', target: '.tour-target-cookie-preview' },
          { icon: '💾', title: 'Save updates', body: 'Save your preferred settings when you are ready to apply the new content.', target: '.tour-target-cookie-save' },
          { icon: '🔗', title: 'Embed code', body: 'Copy the generated embed code when you are ready to publish the cookie widget.', target: '.tour-target-cookie-embed' },
        );
      } else if (p.startsWith('/manager/notification')) {
        baseSteps.push(
          { icon: '🔔', title: 'Notices center', body: 'Manage the notices shown to users and review the content in one place.', target: '.base-main' },
          { icon: '💾', title: 'Save All', body: 'Click here to save every notice card at once to the server.', target: '.tour-target-notif-saveall' },
          { icon: '➕', title: 'Add Notices', body: 'Create a brand new privacy notice — choose a title, consent type, and write the content.', target: '.tour-target-notif-add' },
          { icon: '⬅️', title: 'Back', body: 'Go back to the previous screen.', target: '.tour-target-notif-back' },
          { icon: '📋', title: 'Notice cards', body: 'Each card represents one privacy notice. Click a card to select it for editing.', target: '.tour-target-notif-grid' },
          { icon: '👁️', title: 'View notice', body: 'Click the eye icon to view the full notice content.', target: '.tour-target-notif-view' },
          { icon: '✏️', title: 'Edit notice', body: 'Click the pencil icon to edit this notice\'s content.', target: '.tour-target-notif-edit' },
          { icon: '🗂️', title: 'Sub-categories', body: 'Click this icon to view sub-category details for this notice.', target: '.tour-target-notif-sub' },
        );

      } else if (p.startsWith('/manager/data_types')) {
        baseSteps.push(
          { icon: '🪪', title: 'Data Types', body: 'This page lists all ID proof / data types your organization collects consent for.', target: '.base-main' },
          { icon: '➕', title: 'Add Data Type', body: 'Click here to create a new data type — give it a name, description, and color.', target: '.btn-add-data' },
          { icon: '⬅️', title: 'Back', body: 'Go back to the previous screen.', target: '.btn-back' },
          { icon: '🎴', title: 'Data type cards', body: 'Each card represents one data type. Hover over a card to see view and edit icons.', target: '.cards-grid' },
          { icon: '👁️', title: 'View / Edit a card', body: 'Hover over any card to reveal the view (eye) and edit (pencil) icons, then click to open details.', target: '.consent-card' },
        );
      } else {
        baseSteps.push(
          { icon: '📊', title: 'Manager Dashboard', body: 'Access all manager features from the top navigation bar.', target: '.base-menu' },
          { icon: '👥', title: 'User Management', body: 'Manage accounts, roles, and status.', target: 'a[href="/manager/users"]' },
          { icon: '📋', title: 'Consent Forms', body: 'Manage custom consent forms and versions.', target: 'a[href="/manager/consent"]' },
        );
      }
    } else {
      // User role
      if (p.startsWith('/user/consent-overview')) {
        baseSteps.push(
          { icon: '🛡️', title: 'Consent Overview Banner', body: 'Track, manage, and review all active consent records across your organization from this centralized dashboard.', target: '.co-header-banner' },
          { icon: '📊', title: 'Overall Status Metrics', body: 'Click any metric button (Total, Accepted, Declined, Reconsent, Revoked, Expiry Soon) to instantly filter records by that status.', target: '.consent-stats-card' },
          { icon: '🗂️', title: 'Consent Categories', body: 'Consents are organized into Employee, Vendor, Partner, and Customer categories for structured role-based management.', target: '.co-section-title' },
          { icon: '👥', title: 'Category Breakdown Cards', body: 'Each card displays live counts for Accepted, Declined, Revoked, and Expiring consents. Click any card to expand its detailed consent types.', target: '.co-cards-grid' },
          { icon: '📖', title: 'Interactive Page Guide', body: 'Click the Page Guide button anytime to replay this tour and explore all features.', target: '.co-header-banner button' },
        );

      } else if (p.startsWith('/user/consent')) {
        baseSteps.push(
          { icon: '➕', title: 'Record Consent', body: 'Click this + button to open the form and record a brand new consent for a user.', target: '.tour-target-new-consent-btn' },
          { icon: '🔍', title: 'Search consents', body: 'Search records by name, email, phone, or consent type.', target: '.tour-target-consent-search' },
          { icon: '🏷️', title: 'Filter by status', body: 'Filter the table to show only Accepted, Declined, Rejected, Revoked, or Pending consents.', target: '.tour-target-status-filter' },
          { icon: '📤', title: 'Bulk CSV upload', body: 'Click here to download a sample CSV template and upload multiple consent records at once.', target: '.tour-target-csv-upload-btn' },
          { icon: '📜', title: 'User Logs', body: 'View activity logs for all users from this button.', target: '.tour-target-user-logs-btn' },
          { icon: '⬅️', title: 'Back', body: 'Go back to the previous page.', target: '.tour-target-back-btn' },
          { icon: 'ℹ️', title: 'Legend', body: 'Click here to see what each status color and icon in the table means.', target: '.tour-target-legend-btn' },
          { icon: '🔢', title: 'Pagination controls', body: 'Change how many rows show per page, and move between pages using Prev/Next.', target: '.tour-target-pagination-bar' },
          { icon: '⚡', title: 'Bulk actions', body: 'After selecting one or more rows using the checkboxes, use these buttons to send a bulk email, WhatsApp message, or privacy notice.', target: '.tour-target-bulk-actions' },
          { icon: '📋', title: 'Consents table', body: 'Each row shows one consent record with its status, expiry, and available actions.', target: '.tour-target-all-records, .consent-table-modern, table' },
          { icon: '✏️', title: 'Edit consent', body: 'Click the pencil icon to edit this consent record\'s details.', target: '.tour-target-details-btn, .modern-icon-btn.file-icon-btn' },
          { icon: '⋮', title: 'More options', body: 'Click the ⋮ icon to send this consent via WhatsApp, Email, or Privacy Notice.', target: '.tour-target-bulk-actions, .modern-icon-btn.more-icon-btn' },
          { icon: '🚫', title: 'Revoke consent', body: 'Click this icon to raise a revoke (erasure) request for this consent — it will appear on the DSAR Dashboard.', target: '.status-badge-dot, .tour-target-status-filter, .modern-icon-btn.revoke-icon-btn' },
          { icon: '👁️', title: 'View details', body: 'Click this icon next to a name to see the full details of that consent record.', target: '.tour-target-details-btn' },
          { icon: '📄', title: 'View full consent text', body: 'Click this icon in the Consent column to read the complete consent message.', target: '.tour-target-view-consent-btn' },
        );
      } else if (p.startsWith('/user/dashboard')) {
        baseSteps.push(
          { icon: '📊', title: 'Consent Dashboard', body: 'Get a bird\'s-eye view of all consent activity, compliance status, and trends for your organization.', target: '.db-root, .base-main' },
          { icon: '📅', title: 'Date range filter', body: 'Narrow the dashboard down to a specific date range using From and To.', target: '.tour-target-date-filter' },
          { icon: '👤', title: 'Filter by role', body: 'Switch between All roles, Employee, Vendor, or Partner to see role-specific data.', target: '.tour-target-role-select' },
          { icon: '🔄', title: 'Refresh', body: 'Click here to reload the latest consent data from the server.', target: '.tour-target-refresh-btn' },
          { icon: '🔢', title: 'Metric cards', body: 'Click any card (Total, Accepted, Declined, Pending, Revoked, Adults, Children, Expiring, Expired) to filter and view matching records.', target: '.tour-target-metric-cards' },
          { icon: '⚖️', title: 'Regulatory compliance', body: 'Click a GDPR, DPDP, or DORA card to see a detailed pass/fail breakdown for that framework.', target: '.compliance-section' },
          { icon: '🍩', title: 'Channel & ID proof charts', body: 'Click any slice in these donut charts to view the underlying records for that channel or ID proof type.', target: '.tour-target-donut-charts' },
          { icon: '📊', title: 'Revoked & consent-type charts', body: 'Click any bar to drill into records revoked via that channel or grouped by consent type.', target: '.tour-target-bar-charts' },
          { icon: '📈', title: 'Monthly trend', body: 'Click any point on this chart to see consents and revocations recorded that month.', target: '.tour-target-monthly-chart' },
          { icon: '📋', title: 'All records table', body: 'Browse every consent record here — click a row to open its full details.', target: '.tour-target-all-records' },
        );
      } else if (p.startsWith('/user/custom-report')) {
        baseSteps.push(
          { icon: '📊', title: 'Custom report builder', body: 'Combine data from Consent, DSAR, and Cookies to build a custom report.', target: '.base-main' },
          { icon: '➕', title: 'Add applications', body: 'Choose which application (Consent, DSAR, Cookies) to pull data from. You can add more than one and merge them.', target: '.tour-target-add-app' },
          { icon: '⚡', title: 'Smart filters', body: 'Quickly narrow results by a specific email or tenant ID before generating the report.', target: '.tour-target-smart-filters' },
          { icon: '📝', title: 'Report name', body: 'Give your report a name so it\'s easy to recognize later.', target: '.cr-sb-input' },
          { icon: '📋', title: 'Choose fields', body: 'Click here to pick which columns to include in your report, and drag to reorder them.', target: '.tour-target-fields-section' },
          { icon: '⚙️', title: 'Advanced filters', body: 'Add extra field-level conditions (contains, equals, greater than, etc.) beyond the smart filters.', target: '.tour-target-filters-section' },
          { icon: '➕', title: 'Add condition', body: 'Click here to add a new filter condition to your report.', target: '.tour-target-add-condition' },
          { icon: '🔒', title: 'Report permissions', body: 'Choose who should have access to view this report — by consent type, entity type, or DSAR category.', target: '.tour-target-perms-section' },
          { icon: '🚀', title: 'Generate Report', body: 'Once your sources, fields, and filters are set, click here to build and preview the report.', target: '.cr-btn-primary' },
          { icon: '📥', title: 'Export options', body: 'After generating, use these buttons at the bottom to export your report as CSV or PDF.', target: '.cr-action-bar' },
        );
      } else if (p.startsWith('/user/cookies-notify')) {
        baseSteps.push(
          { icon: '🍪', title: 'Cookie Consent Events', body: 'View and manage all cookie consent events recorded across your applications.', target: '.base-main' },
          { icon: '🔍', title: 'Search', body: 'Search events by app name, status, or URL.', target: '.tour-target-cookie-search' },
          { icon: '📊', title: 'Push Consent stats', body: 'Send consent statistics to the external OneDPDP dashboard.', target: '.tour-target-push-consent' },
          { icon: '📈', title: 'Push Consent Variance', body: 'Push detailed per-record consent variance data to the external system.', target: '.tour-target-push-variance' },
          { icon: '🔄', title: 'Push Cookies stats', body: 'Send cookie consent statistics to the external dashboard.', target: '.tour-target-push-cookies' },
          { icon: '🔲', title: 'Grid / List view', body: 'Switch between grid and list layouts for the cookie events below.', target: '.tour-target-view-toggle' },
          { icon: '🌐', title: 'Web App Consent', body: 'Click the globe icon to filter and view only web-app (Portal) consent events, and create new consent requests.', target: '.icon-btn.webapp' },
          { icon: '🔄', title: 'Refresh', body: 'Reload the latest cookie consent events from the server.', target: '.tour-target-cookie-refresh' },
          { icon: '📋', title: 'Cookie events', body: 'Each card shows one cookie consent event with its app, status, IP, and expiry.', target: '.tour-target-cookie-cards' },
        );
      } else if (p.startsWith('/user/notices')) {
        baseSteps.push(
          { icon: '🔔', title: 'Notices', body: 'This page shows all Privacy Notices along with their consent records.', target: '.base-main' },
          { icon: '🗂️', title: 'Sidebar', body: 'The left sidebar lists all Privacy Notices. Click any notice to load its consent statistics.', target: '#tour-sidebar' },
          { icon: '🔗', title: 'View Notice Link', body: 'Clicking "View notice ↗" opens the original notice in a new tab.', target: '#tour-view-notice' },
          { icon: '🔢', title: 'Sent Counter', body: 'This counter shows the total number of emails sent successfully so far. It updates automatically after every new send.', target: '#tour-sent-counter' },
          { icon: '🚨', title: 'Data Breaches Button', body: 'Clicking the 🔔 Data Breaches button opens the breach templates page, from where you can also send breach notices.', target: '#tour-breach-btn' },
          { icon: '📊', title: 'Stat Cards', body: 'Click on the Total / Accepted / Declined / Revoked cards to filter the table by that status. Click again to remove the filter.', target: '#tour-stats-grid' },
          { icon: '👤', title: 'Role Cards', body: 'Click the Employee / Vendor / Partners card to show only records for that role in the table.', target: '#tour-role-grid' },
          { icon: '☑️', title: 'Select All Checkbox', body: 'The checkbox in the table header selects all visible rows at once.', target: '#tour-select-all' },
          { icon: '✅', title: 'Row Checkbox', body: 'Each row checkbox lets you select individual users for sending bulk emails.', target: '.np-checkbox' },
          { icon: '👤', title: 'User Name Link', body: 'Clicking a user\'s name opens a modal with their full details (email, phone, status, consent preview).', target: '.np-user-link' },
          { icon: '✉️', title: 'Email Selected', body: 'This button sends emails only to the users whose checkboxes are ticked. It stays disabled until at least one row is selected.', target: '#tour-email-selected' },
          { icon: '🧹', title: 'Clear Button', body: 'The Clear button deselects all currently selected rows.', target: '#tour-clear-btn' },
          { icon: '⚡', title: 'Email All', body: 'The ⚡ Email All button sends emails to all rows currently visible under the active filter (after a confirmation popup).', target: '#tour-email-all' },
          { icon: '👁️', title: 'Row Click / Preview', body: 'Clicking any table row opens a preview panel on the side showing the notice\'s email template.', target: '.np-tr' },
          { icon: '✉️', title: 'Send Email (Preview)', body: 'The "✉ Send Email" button inside the preview panel sends the email only to that selected user.', target: '#tour-preview-send' },
          { icon: '✕', title: 'Close Preview', body: 'The "✕ Close" button closes the preview panel.', target: '#tour-preview-close' },
          { icon: '✉️', title: 'Send Email (User Details)', body: 'The Send Email button inside the User Details modal sends the notice email only to that specific user and then closes the modal.', target: '#tour-userdetails-send' },
        );
      } else if (p.startsWith('/user/dps')) {
        baseSteps.push(
          { icon: '🛡️', title: 'DSAR Overview', body: 'This page shows all Data Subject Access Requests, their status, and SLA tracking.', target: '.np-main, main' },
          { icon: '🧭', title: 'Sidebar Navigation', body: 'Switch between My Requests, New Request, Your Rights, Requester Summary, and Help from here.', target: '[data-tour^="nav-"]' },
          { icon: '↔️', title: 'Collapse Sidebar', body: 'Click to collapse or expand the sidebar for more screen space.', target: '[data-tour="sidebar-collapse-btn"]' },
          { icon: '➕', title: '+ New Request', body: 'Click this button to submit a new DSAR request.', target: '[data-tour="header-new-request-btn"]' },
          { icon: '🔄', title: '↻ Refresh', body: 'Fetches the latest DSAR data from the server.', target: '[data-tour="refresh-btn"]' },
          { icon: '↑', title: '↑ Push to OneDPDP', body: 'Syncs and pushes DSAR statistics to the OneDPDP system.', target: '[data-tour="push-onedpdp-btn"]' },
          { icon: '🗑️', title: 'Request Delete Data', body: 'Sends a DSPM erase-and-push request for this tenant.', target: '[data-tour="delete-data-btn"]' },
          { icon: '⬅️', title: '← Back', body: 'Takes you back to the previous page.', target: '[data-tour="back-btn"]' },
          { icon: '📊', title: 'Stat Cards', body: 'Click Total, Open, In Progress, Approved, or Rejected to filter the table by that status.', target: '[data-tour^="stat-"]' },
          { icon: '🔍', title: 'Search Box', body: 'Search instantly by name or email to find a specific request.', target: '[data-tour="search-input"]' },
          { icon: '⏱️', title: 'SLA Selector', body: 'Choose the number of days (7/14/30/60) used to calculate SLA breaches.', target: '[data-tour="sla-select"]' },
          { icon: '⚙️', title: 'Row Action Icons', body: 'Use the ⚙/✓ icon to create a Jira ticket, the ✓ icon to approve the request, and the ✉ icon to send a breach email.', target: '[data-tour="row-jira-btn"]' },
          { icon: '👤', title: 'Name / Email Link', body: 'Click any row\'s name or email to open the full details of that request in a modal.', target: '[data-tour="row-name-link"]' },
          { icon: '🚨', title: 'SLA Column', body: 'Shows whether a request is within SLA or has been breached; click ✉ to send an email if it\'s breached.', target: '[data-tour="sla-badge"]' },
          { icon: '🎟️', title: 'Jira Column', body: 'Click the 🎟 icon to create a Jira ticket, or click ↗ to open an existing ticket.', target: '[data-tour="jira-create-btn"]' },
          { icon: '🔢', title: 'Consent Count Badge', body: 'Click this number to view all consent records for that user.', target: '[data-tour="consent-count-badge"]' },
          { icon: '📄', title: 'Pagination', body: 'Click Prev/Next or a page number to browse more records in the table.', target: '[data-tour="pagination-prev-btn"]' },
          { icon: '📊', title: 'Requester Summary', body: 'Click any email to see all requests from that requester in one place, along with status filters.', target: '[data-tour="requester-email-link"]' },
        );
      } else if (p.startsWith('/user/purpose-consent')) {
        baseSteps.push(
          { icon: '🎯', title: 'Guided Consent Flow', body: 'Follow each step in this flow to complete your consent decision clearly — from privacy notice, to purpose selection, to review and confirmation.', target: '.base-main' },
          { icon: '✉️', title: 'Add Receipt Email', body: 'Type an email address here to receive the consent receipt. Press Enter or comma after each address.', target: '[data-tour="receipt-email-input"]' },
          { icon: '➕', title: '+ Add Button', body: 'Click this button to add the email address you typed, along with the recipient\'s name, phone, and DOB.', target: '[data-tour="add-email-btn"]' },
          { icon: '👤', title: 'Recipient Name', body: 'Enter the full name of the person receiving this consent receipt. This is required before adding an email.', target: '[data-tour="recipient-name-input"]' },
          { icon: '📞', title: 'Recipient Phone', body: 'Enter a valid 10-digit phone number for the recipient. This is required before adding an email.', target: '[data-tour="recipient-phone-input"]' },
          { icon: '📅', title: 'Recipient DOB', body: 'Optionally enter the recipient\'s date of birth.', target: '[data-tour="recipient-dob-input"]' },
          { icon: '🏷️', title: 'Added Recipient', body: 'Each added recipient appears here as a chip showing their name, email, phone, and DOB.', target: '[data-tour="email-chip"]' },
          { icon: '✕', title: 'Remove Recipient', body: 'Click the ✕ icon on a chip to remove that recipient from the list.', target: '[data-tour="remove-email-btn"]' },
          { icon: '✅', title: 'Confirm Choices', body: 'Review each step carefully — your purpose selections and recipient details — before submitting.', target: '.base-main' },
        );
      } else {
        baseSteps.push(
          { icon: '💼', title: 'User Workspace', body: 'Access your consent overview, reports, and notices from the top menu.', target: '.base-menu' },
        );
      }
    }

    return baseSteps;
  };

  /** Map the current URL path to a human-readable section name for the tour card header */
  const getTourSection = (pathname) => {
    const p = normalizeTourPath(pathname).toLowerCase();
    if (p.includes('/user/dashboard')) return 'Dashboard';
    if (p.includes('/user/consent-overview')) return 'Consent Overview';
    if (p.includes('/user/purpose-consent')) return 'Purpose Consent';
    if (p.includes('/user/consent')) return 'Consents';
    if (p.includes('/user/dps')) return 'DSAR';
    if (p.includes('/user/notices')) return 'Notices';
    if (p.includes('/user/custom-report')) return 'Reports';
    if (p.includes('/user/cookies-notify')) return 'Cookies';
    if (p.includes('/user/consent-banner')) return 'Consent Banner';
    if (p.includes('/manager/users')) return 'User Management';
    if (p.includes('/manager/consent')) return 'Consent Forms';
    if (p.includes('/manager/cookie')) return 'Banner';
    if (p.includes('/manager/notification')) return 'Notices';
    if (p.includes('/manager/data_types')) return 'Data Types';
    if (p.includes('/manager/department')) return 'Departments';
    if (p.includes('/manager/dsar')) return 'DSAR Forms';
    if (p.includes('/manager')) return 'Manager';
    if (p.includes('/admin')) return 'Admin';
    return 'Guide';
  };

  const steps = useMemo(() => buildInteractiveGuideSteps(currentPath, user?.role), [currentPath, user?.role]);

  /** Navigate to the role's home page when the logo is clicked */
  function handleLogoClick(e) {
    e.preventDefault();
    setMobileMenuOpen(false);
    if (user?.role === 'admin') {
      const nextPath = '/admin/tenants';
      window.history.pushState({}, '', nextPath);
      setCurrentPath(nextPath);
      if (onMenu) onMenu('tenants');
    } else if (user?.role === 'manager') {
      const nextPath = '/manager';
      window.history.pushState({}, '', nextPath);
      setCurrentPath(nextPath);
      if (setManagerPage) setManagerPage('dashboard');
    } else if (user?.role === 'user') {
      const nextPath = '/user/consent-overview';
      window.history.pushState({}, '', nextPath);
      setCurrentPath(nextPath);
      if (onMenu) onMenu('consentoverview');
    }
    try {
      window.dispatchEvent(new PopStateEvent('popstate'));
    } catch (_) { }
  }

  function closeMenu() {
    setMobileMenuOpen(false);
    setConsentDropdownOpen(false);
    setManagerDropdownOpen(false);
    setCookieDropdownOpen(false);
    setUserCookiesDropdownOpen(false);
    setUserDropdownOpen(false);
  }

  const resetTourHighlight = useCallback(() => {
    document.querySelectorAll('.tour-highlight').forEach((el) => {
      el.classList.remove('tour-highlight', 'tour-badge-bottom');
      if (el.dataset.tourOriginalPosition !== undefined) {
        if (el.dataset.tourOriginalPosition === '') el.style.removeProperty('position');
        else el.style.position = el.dataset.tourOriginalPosition;
        delete el.dataset.tourOriginalPosition;
      }
      if (el.dataset.tourOriginalZIndex !== undefined) {
        if (el.dataset.tourOriginalZIndex === '') el.style.removeProperty('z-index');
        else el.style.zIndex = el.dataset.tourOriginalZIndex;
        delete el.dataset.tourOriginalZIndex;
      }
      if (el.dataset.tourOriginalBg !== undefined) {
        if (el.dataset.tourOriginalBg === '') el.style.removeProperty('background-color');
        else el.style.backgroundColor = el.dataset.tourOriginalBg;
        delete el.dataset.tourOriginalBg;
      }
    });

    document.querySelectorAll('.tour-parent-elevated').forEach((parent) => {
      parent.classList.remove('tour-parent-elevated');
      if (parent.dataset.tourOriginalZIndex !== undefined) {
        if (parent.dataset.tourOriginalZIndex === '') parent.style.removeProperty('z-index');
        else parent.style.zIndex = parent.dataset.tourOriginalZIndex;
        delete parent.dataset.tourOriginalZIndex;
      }
      if (parent.dataset.tourOriginalPosition !== undefined) {
        if (parent.dataset.tourOriginalPosition === '') parent.style.removeProperty('position');
        else parent.style.position = parent.dataset.tourOriginalPosition;
        delete parent.dataset.tourOriginalPosition;
      }
      if (parent.dataset.tourOriginalOverflow !== undefined) {
        if (parent.dataset.tourOriginalOverflow === '') parent.style.removeProperty('overflow');
        else parent.style.overflow = parent.dataset.tourOriginalOverflow;
        delete parent.dataset.tourOriginalOverflow;
      }
    });
  }, []);

  const [spotlightRect, setSpotlightRect] = useState(null);
  const [cardPosState, setCardPosState] = useState({ top: 100, left: 100, arrowLeft: 50, arrowTop: 50, arrowDir: 'bottom' });
  const tourCardRef = useRef(null);

  const closeTour = useCallback((markCompleted = true) => {
    setTourOpen(false);
    setTourStepIndex(0);
    setSpotlightRect(null);
    resetTourHighlight();
    document.body.classList.remove('tour-active');
    if (markCompleted) {
      try { localStorage.setItem('onedpdp_tour_completed', '1'); } catch (_) { }
    }
    window.requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
  }, [resetTourHighlight]);

  const updateTourPosition = useCallback(() => {
    if (!tourOpen) return;
    resetTourHighlight();

    const currentStep = steps[tourStepIndex];
    if (!currentStep) return;

    let targetElement = null;
    const target = currentStep.target;
    if (typeof target === 'string') {
      const selectors = target.split(',').map(s => s.trim());
      for (const sel of selectors) {
        try {
          const found = document.querySelector(sel);
          if (found && isVisibleElement(found)) {
            targetElement = found;
            break;
          }
        } catch (e) {
          // ignore selector parse error
        }
      }
    } else if (target) {
      targetElement = target;
    }

    // Fallback to active header menu item or page header if specific target element is not found
    if (!targetElement || !isVisibleElement(targetElement)) {
      targetElement = document.querySelector('.base-menu .active, .base-menu a, .logo-link, button.btn-primary') || document.body;
    }

    if (!targetElement) return;

    // Instant scroll target element into view
    if (targetElement !== document.body && targetElement !== document.documentElement) {
      targetElement.scrollIntoView({ behavior: 'auto', block: 'center' });
    }

    // Apply highlight class and elevate parent stack
    if (targetElement !== document.body && targetElement !== document.documentElement) {
      targetElement.classList.add('tour-highlight');
      const rawRectTemp = targetElement.getBoundingClientRect();
      if (rawRectTemp.top < 55) {
        targetElement.classList.add('tour-badge-bottom');
      } else {
        targetElement.classList.remove('tour-badge-bottom');
      }
      targetElement.dataset.tourOriginalPosition = targetElement.style.position || '';
      targetElement.dataset.tourOriginalZIndex = targetElement.style.zIndex || '';
      targetElement.dataset.tourOriginalBg = targetElement.style.backgroundColor || '';

      targetElement.style.position = targetElement.style.position || 'relative';
      targetElement.style.zIndex = '2005';

      const computedBg = window.getComputedStyle(targetElement).backgroundColor;
      if (computedBg === 'transparent' || computedBg === 'rgba(0, 0, 0, 0)' || computedBg.includes('rgba(0, 0, 0, 0)')) {
        targetElement.style.backgroundColor = '#ffffff';
      }

      let curr = targetElement.parentElement;
      let depth = 0;
      while (curr && curr !== document.body && curr !== document.documentElement && depth < 6) {
        const compStyle = window.getComputedStyle(curr);
        const clipsTarget = compStyle.overflow !== 'visible' || compStyle.overflowY !== 'visible' || compStyle.overflowX !== 'visible';
        if (clipsTarget) {
          curr.classList.add('tour-parent-elevated');
          if (curr.dataset.tourOriginalZIndex === undefined) {
            curr.dataset.tourOriginalZIndex = curr.style.zIndex || '';
            curr.style.zIndex = '2001';
          }
          if (curr.dataset.tourOriginalPosition === undefined) {
            curr.dataset.tourOriginalPosition = curr.style.position || '';
            if (compStyle.position === 'static') {
              curr.style.position = 'relative';
            }
          }
          if (curr.dataset.tourOriginalOverflow === undefined) {
            curr.dataset.tourOriginalOverflow = curr.style.overflow || '';
            curr.style.overflow = 'visible';
          }
        }
        curr = curr.parentElement;
        depth += 1;
      }
    }

    const rawRect = targetElement.getBoundingClientRect();
    const pad = 6;
    const sRect = {
      left: Math.max(0, rawRect.left - pad),
      top: Math.max(0, rawRect.top - pad),
      width: Math.max(24, rawRect.width + pad * 2),
      height: Math.max(24, rawRect.height + pad * 2),
    };
    setSpotlightRect(sRect);
    const measuredW = tourCardRef.current?.offsetWidth;
    const measuredH = tourCardRef.current?.offsetHeight;
    const cardW = measuredW || Math.min(440, window.innerWidth - 32);
    const cardH = measuredH || 280;
    const screenW = window.innerWidth;
    const screenH = window.innerHeight;
    const GAP = sRect.top < 60 ? 28 : 18;

    const spaceBottom = screenH - (sRect.top + sRect.height);
    const spaceTop = sRect.top;
    const spaceRight = screenW - (sRect.left + sRect.width);
    const spaceLeft = sRect.left;
    const allowSideways = screenW >= 760;

    let placement;
    if (spaceBottom >= cardH + GAP) placement = 'bottom';
    else if (spaceTop >= cardH + GAP) placement = 'top';
    else if (allowSideways && spaceRight >= cardW + GAP) placement = 'right';
    else if (allowSideways && spaceLeft >= cardW + GAP) placement = 'left';
    else placement = spaceBottom >= spaceTop ? 'bottom' : 'top';

    let top, left, arrowDir;
    if (placement === 'bottom') {
      top = sRect.top + sRect.height + GAP;
      left = sRect.left + sRect.width / 2 - cardW / 2;
      arrowDir = 'top';
    } else if (placement === 'top') {
      top = sRect.top - cardH - GAP;
      left = sRect.left + sRect.width / 2 - cardW / 2;
      arrowDir = 'bottom';
    } else if (placement === 'right') {
      left = sRect.left + sRect.width + GAP;
      top = sRect.top + sRect.height / 2 - cardH / 2;
      arrowDir = 'left';
    } else {
      left = sRect.left - cardW - GAP;
      top = sRect.top + sRect.height / 2 - cardH / 2;
      arrowDir = 'right';
    }

    // Clamp so the popover card is always fully inside the visible viewport
    top = Math.max(16, Math.min(screenH - cardH - 16, top));
    left = Math.max(16, Math.min(screenW - cardW - 16, left));

    // Point the arrow caret directly at the target's center
    const targetCenterX = sRect.left + sRect.width / 2;
    const targetCenterY = sRect.top + sRect.height / 2;
    const arrowLeft = Math.max(24, Math.min(cardW - 32, targetCenterX - left - 7));
    const arrowTop = Math.max(24, Math.min(Math.max(cardH - 32, 24), targetCenterY - top - 7));

    setCardPosState({ top, left, arrowLeft, arrowTop, arrowDir });
  }, [tourOpen, tourStepIndex, steps, resetTourHighlight]);

  const navigateToPath = useCallback((path) => {
    if (!path || window.location.pathname === path) return;
    window.history.pushState({}, '', path);
    setCurrentPath(path);

    if (user?.role === 'manager') {
      if (setManagerPage) {
        if (path.includes('/manager/users')) setManagerPage('users');
        else if (path.includes('/manager/consent')) setManagerPage('consent');
        else if (path.includes('/manager/cookie')) setManagerPage('cookie-consent');
        else if (path.includes('/manager/notification')) setManagerPage('notification');
        else if (path.includes('/manager/Data_Types')) setManagerPage('Data_Types');
      }
    } else if (user?.role === 'user') {
      if (onMenu) {
        if (path.includes('/user/consent-overview')) onMenu('consentoverview');
        else if (path.includes('/user/consent')) onMenu('consent');
        else if (path.includes('/user/logs')) onMenu('user-logs');
        else if (path.includes('/user/dashboard')) onMenu('dashboard');
        else if (path.includes('/user/dps')) onMenu('dps');
        else if (path.includes('/user/notices')) onMenu('notices');
        else if (path.includes('/user/cookies-notify')) onMenu('cookies-notify');
        else if (path.includes('/user/custom-report')) onMenu('custom-report');
      }
    }
  }, [user, setManagerPage, onMenu]);

  const handleNextStep = useCallback(() => {
    if (tourStepIndex < steps.length - 1) {
      const nextIdx = tourStepIndex + 1;
      const nextStep = steps[nextIdx];
      if (nextStep && nextStep.route) {
        navigateToPath(nextStep.route);
        setTimeout(() => setTourStepIndex(nextIdx), 60);
      } else {
        setTourStepIndex(nextIdx);
      }
    } else {
      closeTour(true);
    }
  }, [tourStepIndex, steps, navigateToPath, closeTour]);

  const handlePrevStep = useCallback(() => {
    if (tourStepIndex > 0) {
      const prevIdx = tourStepIndex - 1;
      const prevStep = steps[prevIdx];
      if (prevStep && prevStep.route) {
        navigateToPath(prevStep.route);
        setTimeout(() => setTourStepIndex(prevIdx), 60);
      } else {
        setTourStepIndex(prevIdx);
      }
    }
  }, [tourStepIndex, steps, navigateToPath]);

  // First-time user auto tour start (only ever runs once — completion/skip is persisted)
  useEffect(() => {
    let tourDone = null;
    try { tourDone = localStorage.getItem('onedpdp_tour_completed'); } catch (_) { }
    if (!tourDone && user) {
      const timer = setTimeout(() => {
        setTourStepIndex(0);
        setTourOpen(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [user]);

  useEffect(() => {
    if (!tourOpen) {
      setSpotlightRect(null);
      return undefined;
    }

    // Wait a frame so the card has committed/measured before we place it
    const raf = window.requestAnimationFrame(() => updateTourPosition());

    const handleResizeOrScroll = () => {
      window.requestAnimationFrame(updateTourPosition);
    };

    window.addEventListener('resize', handleResizeOrScroll);
    window.addEventListener('scroll', handleResizeOrScroll, true);

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', handleResizeOrScroll);
      window.removeEventListener('scroll', handleResizeOrScroll, true);
    };
  }, [tourOpen, tourStepIndex, updateTourPosition]);

  useEffect(() => {
    document.body.classList.toggle('tour-active', tourOpen);
    return () => {
      document.body.classList.remove('tour-active');
    };
  }, [tourOpen]);

  useEffect(() => {
    if (!tourOpen) return undefined;
    const handler = (event) => {
      if (event.key === 'Escape') closeTour(true);
      else if (event.key === 'ArrowRight') handleNextStep();
      else if (event.key === 'ArrowLeft') handlePrevStep();
    };
    window.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [tourOpen, closeTour, handleNextStep, handlePrevStep]);

  const currentStepData = steps[tourStepIndex];
  const tourProgressPct = steps.length > 0 ? ((tourStepIndex + 1) / steps.length) * 100 : 0;

  return (
    <div className="base-layout">
      <header className="base-header">
        {/* ── LEFT: logo + hamburger + nav ── */}
        <div className="header-left">
          {/* Logo → home */}
          <a
            href="/"
            className="logo-link"
            onClick={handleLogoClick}
            title="Go to Home"
            aria-label="Home"
          >
            <img
              src={process.env.PUBLIC_URL + '/WhitehatsLogo.png'}
              alt="Whitehats Logo"
              className="base-logo"
            />
          </a>

          <button
            className={"mobile-menu-toggle" + (mobileMenuOpen ? ' open' : '')}
            aria-label="Toggle menu"
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <span className="hamburger" />
          </button>

          <div className="logo-separator" />

          {/* ── ADMIN NAV ── */}
          {user?.role === 'admin' && onMenu && (
            <nav className={"base-menu" + (mobileMenuOpen ? ' open' : '')}>
              <NavItem
                href="/admin/tenants"
                icon={faBuilding}
                color="#38bdf8"
                label="Tenants"
                tooltip="Manage all tenant organisations"
                isActive={currentPath === '/admin/tenants'}
                tourPulse={tourOpen && currentPath === '/admin/tenants'}
                onClick={e => { e.preventDefault(); e.stopPropagation(); window.history.pushState({}, '', '/admin/tenants'); onMenu('tenants'); closeMenu(); }}
              />
              <NavItem
                href="/admin/cookie-consent"
                icon={faCookieBite}
                color="#f97316"
                label="Banner"
                tooltip="Cookie consent banner template"
                isActive={currentPath === '/admin/cookie-consent'}
                tourPulse={tourOpen && currentPath === '/admin/cookie-consent'}
                onClick={e => { e.preventDefault(); e.stopPropagation(); window.history.pushState({}, '', '/admin/cookie-consent'); setCurrentPath('/admin/cookie-consent'); onMenu('cookie-consent-template'); closeMenu(); }}
              />
              <NavItem
                href="/dummy_page"
                icon={faFileAlt}
                color="#10b981"
                label="Dummy Consent"
                tooltip="Dummy Consent Template Builder (Sample Only)"
                isActive={currentPath === '/dummy_page' || currentPath === '/admin/dummy_page'}
                tourPulse={tourOpen && (currentPath === '/dummy_page' || currentPath === '/admin/dummy_page')}
                onClick={e => { e.preventDefault(); e.stopPropagation(); window.history.pushState({}, '', '/dummy_page'); setCurrentPath('/dummy_page'); onMenu('dummy-consent'); closeMenu(); }}
              />
            </nav>
          )}

          {/* ── MANAGER NAV ── */}
          {user?.role === 'manager' && setManagerPage && (
            <nav className={"base-menu" + (mobileMenuOpen ? ' open' : '')}>
              <NavItem
                href="/manager/users"
                icon={faUsers}
                color="#60a5fa"
                label="User Management"
                tooltip="Manage users, roles & accounts"
                isActive={currentPath === '/manager/users'}
                tourPulse={tourOpen && currentPath === '/manager/users'}
                onClick={e => { e.preventDefault(); e.stopPropagation(); window.history.pushState({}, '', '/manager/users'); setCurrentPath('/manager/users'); setManagerPage('users'); closeMenu(); }}
              />
              <NavItem
                href="/manager/consent"
                icon={faClipboardList}
                color="#a78bfa"
                label="Consent Forms"
                tooltip="Create & manage consent forms"
                isActive={currentPath === '/manager/consent'}
                tourPulse={tourOpen && currentPath === '/manager/consent'}
                onClick={e => { e.preventDefault(); e.stopPropagation(); window.history.pushState({}, '', '/manager/consent'); setCurrentPath('/manager/consent'); setManagerPage('consent'); closeMenu(); }}
              />
              {/* <NavItem
                href="/manager/market-consent"
                icon={faBullhorn}
                color="#3b82f6"
                label="Market Consent"
                tooltip="External website consent API submissions"
                isActive={currentPath === '/manager/market-consent'}
                tourPulse={tourOpen && currentPath === '/manager/market-consent'}
                onClick={e => { e.preventDefault(); e.stopPropagation(); window.history.pushState({}, '', '/manager/market-consent'); setCurrentPath('/manager/market-consent'); setManagerPage('market-consent'); closeMenu(); }}
              /> */}

              {/* Cookie Consent Dropdown Menu */}
              <div className={`nav-dropdown-container ${cookieDropdownOpen ? 'open' : ''}`}>
                <button
                  type="button"
                  className={`base-menu-item dropdown-toggle ${isCookieDropdownActive ? 'active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleDropdown('cookie');
                  }}
                  aria-expanded={cookieDropdownOpen}
                  aria-haspopup="true"
                >
                  <FontAwesomeIcon icon={faCookieBite} className="nav-icon" style={{ color: '#f97316' }} />
                  <span className="nav-label">Banner</span>
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className={`dropdown-chevron ${cookieDropdownOpen ? 'open' : ''}`}
                    style={{ fontSize: '0.72rem', marginLeft: '0.2rem', pointerEvents: 'none' }}
                  />
                </button>

                <div className="nav-dropdown-menu">
                  <a
                    href="/manager/cookie-consent"
                    className={`nav-dropdown-item ${currentPath === '/manager/cookie-consent' ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.history.pushState({}, '', '/manager/cookie-consent');
                      setCurrentPath('/manager/cookie-consent');
                      setManagerPage('cookie-consent-template');
                      setCookieDropdownOpen(false);
                      closeMenu();
                    }}
                  >
                    <FontAwesomeIcon icon={faCookieBite} className="nav-icon" style={{ color: '#f97316' }} />
                    <span>Cookie Consent</span>
                  </a>

                  <a
                    href="/manager/consent-banner"
                    className={`nav-dropdown-item ${currentPath === '/manager/consent-banner' ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.history.pushState({}, '', '/manager/consent-banner');
                      setCurrentPath('/manager/consent-banner');
                      setManagerPage('consent-banner-template');
                      setCookieDropdownOpen(false);
                      closeMenu();
                    }}
                  >
                    <FontAwesomeIcon icon={faCookieBite} className="nav-icon" style={{ color: '#f87171' }} />
                    <span>Consent Banner</span>
                  </a>
                </div>
              </div>

              {/* <NavItem
                href="/manager/DSAR"
                icon={faIdCard}
                color="#34d399"
                label="DSAR Forms"
                tooltip="Data Subject Access Request forms"
                isActive={currentPath === '/manager/DSAR'}
                tourPulse={tourOpen && currentPath === '/manager/DSAR'}
                onClick={e => { e.preventDefault(); e.stopPropagation(); window.history.pushState({}, '', '/manager/DSAR'); setCurrentPath('/manager/DSAR'); setManagerPage('DSAR'); closeMenu(); }}
              /> */}

              {/* Manager Dropdown: Notices, ID Proof Types, Department */}
              <div className={`nav-dropdown-container ${managerDropdownOpen ? 'open' : ''}`}>
                <button
                  type="button"
                  className={`base-menu-item dropdown-toggle ${isManagerDropdownActive ? 'active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleDropdown('manager');
                  }}
                  aria-expanded={managerDropdownOpen}
                  aria-haspopup="true"
                >
                  <FontAwesomeIcon icon={faBuilding} className="nav-icon" style={{ color: '#38bdf8' }} />
                  <span className="nav-label">More</span>
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className={`dropdown-chevron ${managerDropdownOpen ? 'open' : ''}`}
                    style={{ fontSize: '0.72rem', marginLeft: '0.2rem', pointerEvents: 'none' }}
                  />
                </button>

                <div className="nav-dropdown-menu">
                  <a
                    href="/manager/notification"
                    className={`nav-dropdown-item ${currentPath === '/manager/notification' ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.history.pushState({}, '', '/manager/notification');
                      setCurrentPath('/manager/notification');
                      setManagerPage('notification');
                      setManagerDropdownOpen(false);
                      closeMenu();
                    }}
                  >
                    <FontAwesomeIcon icon={faBell} className="nav-icon" style={{ color: '#facc15' }} />
                    <span>Notices</span>
                  </a>

                  <a
                    href="/manager/Data_Types"
                    className={`nav-dropdown-item ${currentPath === '/manager/Data_Types' ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.history.pushState({}, '', '/manager/Data_Types');
                      setCurrentPath('/manager/Data_Types');
                      setManagerPage('Data_Types');
                      setManagerDropdownOpen(false);
                      closeMenu();
                    }}
                  >
                    <FontAwesomeIcon icon={faDatabase} className="nav-icon" style={{ color: '#a78bfa' }} />
                    <span>Data Types</span>
                  </a>

                  <a
                    href="/manager/Department"
                    className={`nav-dropdown-item ${currentPath === '/manager/Department' ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.history.pushState({}, '', '/manager/Department');
                      setCurrentPath('/manager/Department');
                      setManagerPage('Department');
                      setManagerDropdownOpen(false);
                      closeMenu();
                    }}
                  >
                    <FontAwesomeIcon icon={faBuilding} className="nav-icon" style={{ color: '#22d3ee' }} />
                    <span>Department</span>
                  </a>
                </div>
              </div>
            </nav>
          )}

          {/* ── USER NAV ── */}
          {user?.role === 'user' && onMenu && (
            <nav className={"base-menu" + (mobileMenuOpen ? ' open' : '')}>
              <NavItem
                href="/user/dashboard"
                icon={faHome}
                color="#38bdf8"
                label="Dashboard"
                tooltip="Consent analytics & overview"
                isActive={currentPath === '/user/dashboard'}
                tourPulse={tourOpen && currentPath === '/user/dashboard'}
                onClick={e => { e.preventDefault(); e.stopPropagation(); window.history.pushState({}, '', '/user/dashboard'); setCurrentPath('/user/dashboard'); onMenu('dashboard'); closeMenu(); }}
              />

              {/* Consent Dropdown Menu */}
              <div className={`nav-dropdown-container ${consentDropdownOpen ? 'open' : ''}`}>
                <button
                  type="button"
                  className={`base-menu-item dropdown-toggle ${isConsentActive ? 'active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleDropdown('consent');
                  }}
                  aria-expanded={consentDropdownOpen}
                  aria-haspopup="true"
                >
                  <FontAwesomeIcon icon={faFileAlt} className="nav-icon" style={{ color: '#60a5fa' }} />
                  <span className="nav-label">Consent</span>
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className={`dropdown-chevron ${consentDropdownOpen ? 'open' : ''}`}
                    style={{ fontSize: '0.72rem', marginLeft: '0.2rem', pointerEvents: 'none' }}
                  />
                </button>

                <div className="nav-dropdown-menu">
                  <a
                    href="/user/consent"
                    className={`nav-dropdown-item ${currentPath === '/user/consent' ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSelectConsentItem('/user/consent', 'consent');
                    }}
                  >
                    <FontAwesomeIcon icon={faFileAlt} className="nav-icon" style={{ color: '#22d3ee' }} />
                    <span>Consents</span>
                  </a>

                  <a
                    href="/user/consent-overview"
                    className={`nav-dropdown-item ${currentPath === '/user/consent-overview' ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSelectConsentItem('/user/consent-overview', 'consentoverview');
                    }}
                  >
                    <FontAwesomeIcon icon={faCheckCircle} className="nav-icon" style={{ color: '#a78bfa' }} />
                    <span>Consent Overview</span>
                  </a>

                  {/* <a
                    href="/user/purpose-consent"
                    className={`nav-dropdown-item ${currentPath === '/user/purpose-consent' ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSelectConsentItem('/user/purpose-consent', 'purpose-consent');
                    }}
                  >
                    <FontAwesomeIcon icon={faFileAlt} className="nav-icon" style={{ color: '#1fea4b' }} />
                    <span>Purpose Consent</span>
                  </a> */}
                </div>
              </div>

              {/* User Cookies Dropdown Menu */}
              <div className={`nav-dropdown-container ${userCookiesDropdownOpen ? 'open' : ''}`}>
                <button
                  type="button"
                  className={`base-menu-item dropdown-toggle ${isUserCookiesActive ? 'active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleDropdown('userCookies');
                  }}
                  aria-expanded={userCookiesDropdownOpen}
                  aria-haspopup="true"
                >
                  <FontAwesomeIcon icon={faCookieBite} className="nav-icon" style={{ color: '#f97316' }} />
                  <span className="nav-label">Banner</span>
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className={`dropdown-chevron ${userCookiesDropdownOpen ? 'open' : ''}`}
                    style={{ fontSize: '0.72rem', marginLeft: '0.2rem', pointerEvents: 'none' }}
                  />
                </button>

                <div className="nav-dropdown-menu">
                  <a
                    href="/user/cookies-notify"
                    className={`nav-dropdown-item ${currentPath === '/user/cookies-notify' ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.history.pushState({}, '', '/user/cookies-notify');
                      setCurrentPath('/user/cookies-notify');
                      if (onMenu) onMenu('cookies-notify');
                      setUserCookiesDropdownOpen(false);
                      closeMenu();
                    }}
                  >
                    <FontAwesomeIcon icon={faCookieBite} className="nav-icon" style={{ color: '#f59e0b' }} />
                    <span>Cookies</span>
                  </a>

                  <a
                    href="/user/consent-banner"
                    className={`nav-dropdown-item ${currentPath === '/user/consent-banner' ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.history.pushState({}, '', '/user/consent-banner');
                      setCurrentPath('/user/consent-banner');
                      if (onMenu) onMenu('consent-banner');
                      setUserCookiesDropdownOpen(false);
                      closeMenu();
                    }}
                  >
                    <FontAwesomeIcon icon={faCookie} className="nav-icon" style={{ color: '#0b46f5' }} />
                    <span>Consent-Banner</span>
                  </a>
                </div>
              </div>

              <NavItem
                href="/user/dps"
                icon={faShieldAlt}
                color="#34d399"
                label="DSAR"
                tooltip="Data Subject Access Requests"
                isActive={currentPath === '/user/dps'}
                tourPulse={tourOpen && currentPath === '/user/dps'}
                onClick={e => { e.preventDefault(); e.stopPropagation(); window.history.pushState({}, '', '/user/dps'); setCurrentPath('/user/dps'); onMenu('dps'); closeMenu(); }}
              />
              <NavItem
                href="/user/notices"
                icon={faBell}
                color="#facc15"
                label="Notices"
                tooltip="Privacy notices & email broadcasts"
                isActive={currentPath === '/user/notices' || currentPath === '/user/smart-notices'}
                tourPulse={tourOpen && (currentPath === '/user/notices' || currentPath === '/user/smart-notices')}
                onClick={e => { e.preventDefault(); e.stopPropagation(); window.history.pushState({}, '', '/user/notices'); setCurrentPath('/user/notices'); onMenu('notices'); closeMenu(); }}
              />
              <NavItem
                href="/user/custom-report"
                icon={faChartBar}
                color="#c084fc"
                label="Reports"
                tooltip="Build & export custom reports"
                isActive={currentPath === '/user/custom-report'}
                tourPulse={tourOpen && currentPath === '/user/custom-report'}
                onClick={e => { e.preventDefault(); e.stopPropagation(); window.history.pushState({}, '', '/user/custom-report'); setCurrentPath('/user/custom-report'); onMenu('custom-report'); closeMenu(); }}
              />
              {/* <NavItem
                href="/user/market-consent"
                icon={faBullhorn}
                color="#3b82f6"
                label="Market Consent"
                tooltip="External website consent API submissions"
                isActive={currentPath === '/user/market-consent'}
                tourPulse={tourOpen && currentPath === '/user/market-consent'}
                onClick={e => { e.preventDefault(); e.stopPropagation(); window.history.pushState({}, '', '/user/market-consent'); setCurrentPath('/user/market-consent'); onMenu('market-consent'); closeMenu(); }}
              /> */}
            </nav>
          )}
        </div>

        {/* ── RIGHT: action buttons ── */}
        <div className="header-right">

          {/* Smart Consent (user only) */}
          {user?.role === 'user' && (
            <button
              type="button"
              className="header-action-btn smart-consent-btn"
              onClick={e => {
                e.preventDefault(); e.stopPropagation();
                if (onMenu) onMenu('smart-consent');
                try { window.history.pushState({}, '', '/user/smart-consent'); window.dispatchEvent(new PopStateEvent('popstate')); } catch (_) { }
              }}
              title="Smart Consent"
              aria-label="Smart Consent"
            >
              <FontAwesomeIcon icon={faPlus} style={{ color: '#34d399' }} />
              {/* <span className="btn-label">Smart Consent</span> */}
            </button>
          )}

          {/* Subscription (admin / manager) */}
          {(user?.role === 'admin' || user?.role === 'manager') && (
            <button
              className="header-action-btn subscription-btn"
              onClick={e => { e.preventDefault(); e.stopPropagation(); setShowSubscription(true); }}
              title="Subscription"
              aria-label="Subscription"
            >
              <FontAwesomeIcon icon={faCreditCard} style={{ color: '#fbbf24' }} />
              <span className="btn-label">Subscription</span>
            </button>
          )}

          {/* Home (manager) */}
          {user?.role === 'manager' && setManagerPage && (
            <button
              className="header-action-btn home-btn"
              onClick={() => setManagerPage('dashboard')}
              title="Home"
              aria-label="Home"
            >
              <FontAwesomeIcon icon={faHome} style={{ color: '#38bdf8' }} />
              <span className="btn-label">Home</span>
            </button>
          )}

          {/* Home (user) */}
          {user?.role === 'user' && (
            <button
              className="header-action-btn home-btn"
              onClick={() => {
                if (onMenu) onMenu('consentoverview');
                window.history.pushState({}, '', '/user/consent-overview');
                setCurrentPath('/user/consent-overview');
              }}
              title="Home"
              aria-label="Home"
            >
              <FontAwesomeIcon icon={faHome} style={{ color: '#38bdf8' }} />
              <span className="btn-label">Home</span>
            </button>
          )}

          {/* Guided Help → opens Help/PDF modal */}
          <div className="nav-tooltip-wrap">
            <button
              type="button"
              className="header-action-btn help-btn"
              aria-label="Help and documentation"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                setShowHelpModal(true);
              }}
            >
              <FontAwesomeIcon icon={faQuestionCircle} style={{ color: '#f472b6' }} />
              <span className="btn-label">Help</span>
            </button>
            <span className="nav-tooltip-box">Guides, PDFs & page tour</span>
          </div>

          {/* User Profile Dropdown Menu */}
          {user && (() => {
            const displayName = getFormattedUserName(user);
            const displayRole = getFormattedUserRole(user);
            const showRole = displayName.toLowerCase() !== displayRole.toLowerCase();

            return (
              <div className={`user-profile-container ${userDropdownOpen ? 'open' : ''}`}>
                <button
                  type="button"
                  className="user-profile-btn"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleDropdown('user');
                  }}
                  title="User Profile Menu"
                  aria-label="User Profile Menu"
                  aria-expanded={userDropdownOpen}
                  aria-haspopup="true"
                >
                  <div className="user-avatar-circle">
                    <FontAwesomeIcon icon={faUserCircle} />
                  </div>
                  <div className="user-info-text">
                    <span className="user-display-name">{displayName}</span>
                    {showRole && <span className="user-display-role">{displayRole}</span>}
                  </div>
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className={`profile-chevron ${userDropdownOpen ? 'open' : ''}`}
                    style={{ pointerEvents: 'none' }}
                  />
                </button>

                {userDropdownOpen && (
                  <div className="user-profile-dropdown-menu">
                    <div className="user-profile-header">
                      <div className="user-avatar-large">
                        <FontAwesomeIcon icon={faUserCircle} />
                      </div>
                      <div className="user-details">
                        <div className="profile-name">{displayName}</div>
                        {showRole && <div className="profile-role">{displayRole}</div>}
                      </div>
                    </div>

                    <div className="user-profile-divider" />

                    <button
                      type="button"
                      className="user-dropdown-item logout-item"
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setUserDropdownOpen(false);
                        const result = await Swal.fire({
                          title: 'Logout?',
                          text: 'Do you want to logout?',
                          icon: 'question',
                          showCancelButton: true,
                          confirmButtonText: 'Logout',
                          cancelButtonText: 'Cancel',
                          confirmButtonColor: '#dc2626',
                          cancelButtonColor: '#6b7280',
                          reverseButtons: true,
                        });
                        if (result.isConfirmed) onLogout();
                      }}
                    >
                      <FontAwesomeIcon icon={faSignOutAlt} className="menu-item-icon" />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </header>

      <main className="base-main">{children}</main>

      {tourOpen && (
        <>
          {/* ── SVG Spotlight Mask Backdrop ── */}
          <svg className="tour-spotlight-svg">
            {spotlightRect && (
              <defs>
                <mask id="tour-spotlight-mask">
                  <rect x="0" y="0" width="100%" height="100%" fill="white" />
                  <rect
                    x={spotlightRect.left}
                    y={spotlightRect.top}
                    width={spotlightRect.width}
                    height={spotlightRect.height}
                    rx="12"
                    fill="black"
                  />
                </mask>
              </defs>
            )}
            <rect
              x="0"
              y="0"
              width="100%"
              height="100%"
              fill="rgba(4, 7, 20, 0.78)"
              mask={spotlightRect ? "url(#tour-spotlight-mask)" : undefined}
            />
            {spotlightRect && (
              <rect
                x={spotlightRect.left}
                y={spotlightRect.top}
                width={spotlightRect.width}
                height={spotlightRect.height}
                rx="12"
                fill="none"
                stroke="#f59e0b"
                strokeWidth="3.5"
                className="tour-spotlight-pulse-ring"
              />
            )}
          </svg>

          {/* Backdrop click handler to close tour */}
          <div className="tour-backdrop-click" onClick={() => closeTour(true)} aria-hidden="true" />

          {/* ── Floating Callout Popover Card with Arrow Caret ── */}
          <div
            ref={tourCardRef}
            className="tour-card"
            style={{ top: `${cardPosState.top}px`, left: `${cardPosState.left}px` }}
            role="dialog"
            aria-label="Page guide"
            aria-modal="true"
          >
            {/* Top progress bar */}
            <div className="tour-progress-track">
              <div className="tour-progress-fill" style={{ width: `${tourProgressPct}%` }} />
            </div>

            {/* Directional Caret Arrow pointing directly to target point */}
            <div
              className={`tour-card-arrow arrow-${cardPosState.arrowDir}`}
              style={
                cardPosState.arrowDir === 'left' || cardPosState.arrowDir === 'right'
                  ? { top: `${cardPosState.arrowTop}px` }
                  : { left: `${cardPosState.arrowLeft}px` }
              }
            />

            {/* Header: MODULE SECTION tag + page pill + step counter + close */}
            <div className="tour-card-header">
              <div className="tour-card-header-tags">
                <span className="tour-section-tag">FEATURE GUIDE</span>
                <span className="tour-page-pill">{getTourSection(currentPath)}</span>
              </div>
              <div className="tour-card-header-right">
                <span className="tour-step-counter">{tourStepIndex + 1}/{steps.length}</span>
                <button type="button" className="tour-close-btn" onClick={() => closeTour(true)} aria-label="Close guide">×</button>
              </div>
            </div>

            {/* Title */}
            <div className="tour-card-title-wrap">
              <div className="tour-card-eyebrow">STEP {tourStepIndex + 1} OF {steps.length}</div>
              <div className="tour-card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {currentStepData?.icon && (
                  <span className="tour-card-icon" style={{ fontSize: '20px', lineHeight: 1, display: 'inline-flex', alignItems: 'center' }}>
                    {currentStepData.icon}
                  </span>
                )}
                <span>{currentStepData?.title || 'Guide'}</span>
              </div>
            </div>

            {/* Body: WHAT TO DO + EXPECTED OUTCOME */}
            <div className="tour-card-body">
              <div className="tour-what-section">
                <div className="tour-section-label tour-what-label">WHAT TO DO</div>
                <p className="tour-section-text">
                  {currentStepData?.body || 'This guide will explain the current page and its main actions.'}
                </p>
              </div>
              <div className="tour-outcome-section">
                <div className="tour-section-label tour-outcome-label">
                  <span>✓</span> EXPECTED OUTCOME
                </div>
                <p className="tour-section-text">
                  You know where <strong>{(currentStepData?.title || 'this feature').toLowerCase()}</strong> is located and can use it confidently.
                </p>
              </div>
            </div>

            {/* Footer: skip + progress dots + Back / Next buttons */}
            <div className="tour-card-footer">
              <div className="tour-footer-left">
                <button type="button" className="tour-skip-btn" onClick={() => closeTour(true)}>
                  Skip tour
                </button>
                {steps.length <= 8 && (
                  <div className="tour-progress-dots">
                    {steps.map((_, i) => (
                      <div
                        key={i}
                        className={`tour-dot${i === tourStepIndex ? ' active' : i < tourStepIndex ? ' done' : ''}`}
                      />
                    ))}
                  </div>
                )}
              </div>
              <div className="tour-footer-btns">
                <button
                  type="button"
                  className="tour-nav-btn"
                  disabled={tourStepIndex === 0}
                  onClick={handlePrevStep}
                >
                  ← Back
                </button>
                <button
                  type="button"
                  className="tour-nav-btn tour-nav-btn-primary"
                  onClick={handleNextStep}
                >
                  {tourStepIndex < steps.length - 1 ? 'Next →' : 'Finish ✓'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      <SubscriptionModal show={showSubscription} onClose={() => setShowSubscription(false)} user={user} />

      {/* Help PDF Download Modal */}
      {showHelpModal && (
        <HelpPdfModal
          onClose={() => setShowHelpModal(false)}
          role={user?.role}
          onStartTour={() => {
            setTourStepIndex(0);
            setTourOpen(true);
          }}
        />
      )}

      <footer className="base-footer">
        <div className="footer-left">© Whitehats Technologies — 2026 v1.0.0</div>
        <div className="footer-right"><VersionLabel /></div>
      </footer>
    </div>
  );
}

export default BasePage;